import { Message, SendMessageParams, StreamEvent } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const API_V1 = `${API_BASE}/api/workflow`;

export async function sendMessageStream(
  { query, sessionId, email, location }: SendMessageParams,
  onEvent: (event: StreamEvent) => void
): Promise<{ response: string; toolData: unknown[]; extraction: Message['extraction']; encodedPolyline?: string }> {
  const body = {
    query,
    session_id: sessionId,
    email,
    coordinates: location
      ? {
          latitude: location.latitude,
          longitude: location.longitude,
          name: location.name || null,
        }
      : null,
  };

  const response = await fetch(`${API_V1}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    console.error('[API] Streaming request failed with status:', response.status);
    throw new Error(`API error: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResponse = '';
  let finalToolData: unknown[] = [];
  let finalExtraction: Message['extraction'] = {};
  let finalEncodedPolyline: string | undefined = undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) {
          continue;
        }

        let jsonStr = '';
        if (trimmed.startsWith('data: ')) {
          jsonStr = trimmed.substring(6);
        } else if (trimmed.startsWith('{')) {
          jsonStr = trimmed;
        }

        if (jsonStr) {
          try {
            const event: StreamEvent = JSON.parse(jsonStr);
            onEvent(event);

            // Capture encodedPolyline from any event — check all known locations
            const polylineFromEvent =
              event.encodedPolyline ||
              event.route?.encodedPolyline ||
              (event.extraction?.route as any)?.encodedPolyline;
            if (polylineFromEvent) {
              finalEncodedPolyline = polylineFromEvent;
            }

            if (event.type === 'workflow_complete') {
              finalResponse = event.response || '';
              finalToolData = event.toolData || [];
              finalExtraction = event.extraction || {};
            }
          } catch (e) {
            console.warn('[API] Failed to parse event:', jsonStr);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return {
    response: finalResponse,
    toolData: finalToolData,
    extraction: finalExtraction,
    encodedPolyline: finalEncodedPolyline,
  };
}

export async function sendMessage({ query, sessionId, email, location }: SendMessageParams) {
  const body = {
    query,
    session_id: sessionId,
    email,
    coordinates: location
      ? {
          latitude: location.latitude,
          longitude: location.longitude,
          name: location.name || null,
        }
      : null,
  };

  console.log('[API] Sending POST /api/workflow/chat');
  console.log('[API] Request body:', JSON.stringify(body, null, 2));

  const response = await fetch(`${API_V1}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  console.log('[API] Response status:', response.status);

  if (!response.ok) {
    console.error('[API] Request failed with status:', response.status);
    throw new Error(`API error: ${response.status}`);
  }

  const data = await response.json();
  console.log('[API] Response received:', data);
  return data;
}

// Fetch all sessions for a user from backend
export async function fetchSessions(email: string) {
  console.log('[API] Fetching sessions for:', email);
  const response = await fetch(`${API_V1}/sessions?email=${encodeURIComponent(email)}`);
  if (!response.ok) throw new Error(`Failed to fetch sessions: ${response.status}`);
  return response.json();
}

// Create a new session on backend
export async function createSessionAPI(email: string, sessionId: string) {
  console.log('[API] Creating session on backend:', sessionId);
  const response = await fetch(`${API_V1}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, session_id: sessionId }),
  });
  if (!response.ok) throw new Error(`Failed to create session: ${response.status}`);
  return response.json();
}

// Fetch a single session with full chat history from backend
export async function fetchSessionById(email: string, sessionId: string) {
  console.log('[API] Fetching session by id:', sessionId);
  const response = await fetch(
    `${API_V1}/sessions/${sessionId}?email=${encodeURIComponent(email)}`
  );
  if (!response.ok) throw new Error(`Failed to fetch session: ${response.status}`);
  return response.json();
}

// Append messages to a session and optionally update its title
export async function appendMessagesAPI(
  email: string,
  sessionId: string,
  messages: unknown[],
  title: string | null = null
) {
  const body: { email: string; messages: unknown[]; title?: string } = { email, messages };
  if (title) body.title = title;
  const response = await fetch(`${API_V1}/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Failed to update session: ${response.status}`);
  return response.json();
}

// Delete a session from backend
export async function deleteSessionAPI(email: string, sessionId: string) {
  console.log('[API] Deleting session from backend:', sessionId);
  const response = await fetch(`${API_V1}/sessions/${sessionId}?email=${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`Failed to delete session: ${response.status}`);
  return response.json();
}
